import React, { useState, useEffect, useRef } from 'react';
import TableComp, { Column } from '../../../../components/Table'; // Import Table component
import { collection, getDocs, query, orderBy, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiPlus, FiFile, FiDownload, FiEye, FiSearch, FiEdit2, FiCheck, FiX, FiAlertCircle, FiFileText } from 'react-icons/fi';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { formatBAMSEmailBody, formatBAMSPurchaseOrderEmail, generatePurchaseOrderNumber, formatBAMSInstallationEmail } from '../email/bamsEmailService';
import { sendEmailsWithErrorHandling } from '../../../../api/emailService';

type InventoryItem = {
    id: string;
    itemCode?: string;
    itemName?: string;
    itemDescription?: string;
    brandName?: string;
    makeModel?: string;
    rates?: number;
    category?: string;
    [key: string]: any;
};

type SelectedItem = {
    itemId: string;
    itemCode: string;
    itemName: string;
    itemDescription?: string;
    quantity: number;
    rate?: number;
    amount?: number;
    reason?: string;
};

type BAMSRequest = {
    id: string;
    requestId?: string;
    dateOfRequest?: string;
    branchCode?: string;
    branchName?: string;
    branchId?: string;
    vendor?: string;
    vendorId?: string;
    items?: SelectedItem[];
    company?: string;
    companyId?: string;
    requesterName?: string;
    requesterContact?: string;
    status?: string;
    requestCategory?: 'Replacement' | 'Additional' | 'New';
    psoStatus?: 'installed' | 'not installed';
    installationDate?: string;
    remarks?: string;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    [key: string]: any;
};

export default function BAMS() {
    const pso = useAppSelector((s) => s.user.pso);
    const [requests, setRequests] = useState<BAMSRequest[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<BAMSRequest[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<BAMSRequest | null>(null);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [showToast, setShowToast] = useState(false);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editedRequest, setEditedRequest] = useState<BAMSRequest | null>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [showInstallConfirmModal, setShowInstallConfirmModal] = useState(false);
    const [installationDate, setInstallationDate] = useState(new Date().toISOString().split('T')[0]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [taxPercentage, setTaxPercentage] = useState<number>(() => {
        const saved = localStorage.getItem('bams_tax_percentage');
        return saved ? parseFloat(saved) : 18;
    });

    // Email state
    const [showEmailPreview, setShowEmailPreview] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);


    // Form states
    const [formData, setFormData] = useState({
        requestId: '',
        dateOfRequest: new Date().toISOString().split('T')[0],
        branchCode: '',
        branchName: '',
        branchId: '',
        vendor: '',
        vendorId: '',
        selectedItems: [] as SelectedItem[],
        remarks: '',
        requestCategory: '' as 'Replacement' | 'Additional' | 'New' | '',
    });
    const [branches, setBranches] = useState<any[]>([]);
    const [vendors, setVendors] = useState<any[]>([]);
    const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
    const [branchesMap, setBranchesMap] = useState<Record<string, any>>({});
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [branchSearch, setBranchSearch] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');
    const [itemSearch, setItemSearch] = useState('');
    const [itemBrandFilter, setItemBrandFilter] = useState<string>('All');
    const [tempSelectedItems, setTempSelectedItems] = useState<{ [key: string]: InventoryItem }>({});
    const branchModalRef = useRef<HTMLDivElement>(null);
    const vendorModalRef = useRef<HTMLDivElement>(null);

    const uid = auth.currentUser?.uid;
    const psoDesignation = pso?.designation || '';
    const psoName = pso?.name || '';
    const isRegionalPSO = psoDesignation === 'Regional PSO';

    // Check if user can create requests
    // Regular PSO can create requests, but for incharge users, only Incharge MIS and Incharge BAMS can create
    const designation = pso?.designation || '';
    const isIncharge = /incharge/i.test(designation);
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isInchargeMIS = inchargeType.includes('mis');
    const isInchargeBAMS = inchargeType.includes('bams');
    const canCreateRequests = !isIncharge || isInchargeMIS || isInchargeBAMS; // Regular PSO, Incharge MIS, or Incharge BAMS can create

    // Check if user can edit (PSO or CCTV Incharge - BAMS can also be edited by same roles)
    const canEdit = psoDesignation === 'PSO' || psoDesignation?.toLowerCase().includes('cctv');

    // Only incharge users (MIS or BAMS) can approve/reject requests
    const canApproveReject = isInchargeMIS || isInchargeBAMS;

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
        const isBankUser = userRole === 'bank';

        const fetchCompanyId = async () => {
            if (isBankUser) {
                // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                const directBank = await getDoc(doc(db, "banks", uid));
                if (directBank.exists()) {
                    setCompanyId(uid);
                    return;
                }
                // Try query by userId field
                const banksSnap = await getDocs(collection(db, "banks"));
                for (const bankDoc of banksSnap.docs) {
                    const bankData = bankDoc.data();
                    if (bankData.userId === uid) {
                        setCompanyId(bankDoc.id);
                        return;
                    }
                }
            } else {
                // For PSO users, find which bank has this PSO
                const banksSnap = await getDocs(collection(db, 'banks'));
                for (const bankDoc of banksSnap.docs) {
                    const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
                    const posDoc = await getDoc(posDocRef);
                    if (posDoc.exists()) {
                        setCompanyId(bankDoc.id);
                        break;
                    }
                }
            }
        };

        fetchCompanyId();
    }, [uid]);

    // Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const fetchRequests = async () => {
            setLoading(true);
            try {
                // Fetch from hardware_requests collection, not regular requests
                const requestsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'hardware_requests'),
                        orderBy('createdAt', 'desc')
                    )
                );

                const requestsList = requestsSnap.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];

                setRequests(requestsList);

                // Extract unique statuses
                const uniqueStatuses = Array.from(
                    new Set(requestsList.map(req => req.status).filter((s): s is string => Boolean(s)))
                );
                setStatuses(uniqueStatuses);
            } catch (error) {
                console.error('Error fetching requests:', error);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, [companyId]);

    // Fetch branches
    useEffect(() => {
        if (!companyId) return;
        const fetchBranches = async () => {
            try {
                // Fetch locations
                const locSnap = await getDocs(collection(db, "locations"));
                const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

                const branchesSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
                const branchesList = branchesSnap.docs.map(doc => {
                    const branchData = doc.data();
                    return {
                        id: doc.id,
                        ...branchData,
                        locationName: locList.find(l => l.id === branchData.locationId)?.name || "N/A"
                    };
                });
                setBranches(branchesList);

                // Create a map for quick lookup
                const map: Record<string, any> = {};
                branchesList.forEach(branch => {
                    map[branch.id] = branch;
                });
                setBranchesMap(map);
            } catch (error) {
                console.error('Error fetching branches:', error);
            }
        };
        fetchBranches();
    }, [companyId]);

    // Fetch vendors (BAMS companies) from global companies collection
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                // Fetch from global companies collection
                const companiesSnap = await getDocs(collection(db, 'companies'));
                const vendorsList: any[] = [];

                for (const companyDoc of companiesSnap.docs) {
                    const companyData = companyDoc.data();
                    // Filter by companyType === 'BAMS'
                    if (companyData.companyType === 'BAMS') {
                        vendorsList.push({
                            id: companyDoc.id,
                            companyId: companyData.companyId || companyDoc.id,
                            companyName: companyData.companyName,
                            ...companyData
                        });
                    }
                }

                setVendors(vendorsList);
            } catch (error) {
                console.error('Error fetching vendors:', error);
            }
        };
        fetchVendors();
    }, []);

    // Fetch inventory items (BAMS only)
    useEffect(() => {
        if (!companyId) return;
        const fetchInventory = async () => {
            try {
                const itemsSnap = await getDocs(
                    query(
                        collection(db, 'banks', companyId, 'inventory'),
                        orderBy('createdAt', 'desc')
                    )
                );
                const itemsList = itemsSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as InventoryItem[];

                // Filter only BAMS items
                const bamsItems = itemsList.filter(item => item.category?.toUpperCase() === 'BAMS');
                setInventoryItems(bamsItems);
            } catch (error) {
                console.error('Error fetching inventory items:', error);
            }
        };
        fetchInventory();
    }, [companyId]);

    // Generate Request ID
    const generateRequestId = () => {
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `BAMS-${psoInitials}-${timestamp}`;
    };

    // Auto-generate Request ID when modal opens
    useEffect(() => {
        if (showAddModal && !formData.requestId) {
            setFormData(prev => ({ ...prev, requestId: generateRequestId() }));
        }
    }, [showAddModal, psoName]);

    // Close modals on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (branchModalRef.current && !branchModalRef.current.contains(event.target as Node)) {
                setShowBranchModal(false);
            }
            if (vendorModalRef.current && !vendorModalRef.current.contains(event.target as Node)) {
                setShowVendorModal(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset form
    const resetForm = () => {
        setFormData({
            requestId: generateRequestId(),
            dateOfRequest: new Date().toISOString().split('T')[0],
            branchCode: '',
            branchName: '',
            branchId: '',
            vendor: '',
            vendorId: '',
            selectedItems: [],
            remarks: '',
            requestCategory: '',
        });
        setTempSelectedItems({});
    };

    // Handle branch selection
    const handleBranchSelection = (branch: any) => {
        setFormData(prev => ({
            ...prev,
            branchId: branch.id,
            branchCode: branch.branchCode || '',
            branchName: branch.address || branch.name || '',
        }));
        setShowBranchModal(false);
        setBranchSearch('');
    };

    // Handle vendor selection
    const handleVendorSelection = (vendor: any) => {
        setFormData(prev => ({
            ...prev,
            vendorId: vendor.id,
            vendor: vendor.companyName || '',
        }));
        setShowVendorModal(false);
        setVendorSearch('');
    };

    // Handle item selection in modal (temporary)
    const handleItemToggle = (item: InventoryItem) => {
        setTempSelectedItems(prev => {
            if (prev[item.id]) {
                const newState = { ...prev };
                delete newState[item.id];
                return newState;
            } else {
                return { ...prev, [item.id]: item };
            }
        });
    };

    // Handle submit selected items from modal
    const handleSubmitSelectedItems = () => {
        const newItems: SelectedItem[] = Object.values(tempSelectedItems).map(item => ({
            itemId: item.id,
            itemCode: item.itemCode || 'N/A',
            itemName: item.itemName || item.itemDescription || 'N/A',
            itemDescription: item.itemDescription || '',
            quantity: 1,
        }));

        setFormData(prev => ({
            ...prev,
            selectedItems: [...prev.selectedItems, ...newItems]
        }));

        setTempSelectedItems({});
        setItemSearch('');
        setItemBrandFilter('All');
        setShowItemModal(false);
    };

    // Handle opening item modal
    const handleOpenItemModal = () => {
        setTempSelectedItems({});
        setItemSearch('');
        setItemBrandFilter('All');
        setShowItemModal(true);
    };

    // Handle quantity change for selected items
    const handleQuantityChange = (index: number, quantity: number) => {
        if (quantity < 1) {
            setToastMessage('Quantity must be at least 1');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setFormData(prev => ({
            ...prev,
            selectedItems: prev.selectedItems.map((item, i) => {
                if (i === index) {
                    // Recalculate amount if rate exists (for Additional requests)
                    const amount = item.rate ? item.rate * quantity : item.amount;
                    return { ...item, quantity, amount };
                }
                return item;
            })
        }));
    };

    // Remove selected item
    const handleRemoveItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            selectedItems: prev.selectedItems.filter((_, i) => i !== index)
        }));
    };

    // Handle save request
    const handleSaveRequest = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.branchCode || !formData.branchName || !formData.vendor || formData.selectedItems.length === 0 || !formData.requestCategory) {
            setToastMessage('Please fill in all required fields (including Request Category) and add at least one item');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        try {
            // Get region from branch's locationName
            const branch = formData.branchId ? branchesMap[formData.branchId] : null;
            const region = branch?.locationName || '';

            const requestData = {
                requestId: formData.requestId,
                requestType: 'BAMS',
                dateOfRequest: formData.dateOfRequest,
                branchCode: formData.branchCode,
                branchName: formData.branchName,
                branchId: formData.branchId,
                region: region,
                vendor: formData.vendor,
                vendorId: formData.vendorId,
                items: formData.selectedItems,
                remarks: formData.remarks || '',
                requestCategory: formData.requestCategory,
                status: 'pending',
                psoStatus: 'not installed',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: uid,
            };

            await addDoc(collection(db, 'banks', companyId, 'hardware_requests'), requestData);

            setToastMessage('BAMS hardware request created successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            setShowConfirmModal(false);
            resetForm();

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to create request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Filtered branches, vendors, and items
    const filteredBranches = branches.filter(b =>
        b.branchCode?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.address?.toLowerCase().includes(branchSearch.toLowerCase()) ||
        b.name?.toLowerCase().includes(branchSearch.toLowerCase())
    );

    const filteredVendors = vendors.filter(v =>
        v.companyName?.toLowerCase().includes(vendorSearch.toLowerCase()) ||
        v.companyId?.toLowerCase().includes(vendorSearch.toLowerCase())
    );

    const uniqueBrands = Array.from(new Set(inventoryItems.map(item => item.brandName).filter((brand): brand is string => Boolean(brand))));

    const availableItems = inventoryItems.filter(item => {
        const isAlreadySelected = formData.selectedItems.some(selected => selected.itemId === item.id);
        const matchesSearch = !itemSearch ||
            item.itemCode?.toLowerCase().includes(itemSearch.toLowerCase()) ||
            item.itemName?.toLowerCase().includes(itemSearch.toLowerCase()) ||
            item.itemDescription?.toLowerCase().includes(itemSearch.toLowerCase());
        const matchesBrand = itemBrandFilter === 'All' || item.brandName === itemBrandFilter;
        return !isAlreadySelected && matchesSearch && matchesBrand;
    });

    // Set default filter status to 'pending' for Regional PSO
    useEffect(() => {
        if (psoDesignation === 'Regional PSO') {
            setStatusFilter('pending');
        }
    }, [psoDesignation]);

    // Filter requests
    useEffect(() => {
        let filtered = [...requests];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(request => {
                const searchLower = searchTerm.toLowerCase();
                const branch = request.branchId ? branchesMap[request.branchId] : null;
                const region = branch?.locationName || '';
                const itemNames = request.items && request.items.length > 0
                    ? request.items.map(item => item.itemName).join(' ')
                    : '';

                return (
                    request.requestId?.toLowerCase().includes(searchLower) ||
                    request.branchCode?.toLowerCase().includes(searchLower) ||
                    request.branchName?.toLowerCase().includes(searchLower) ||
                    request.requesterName?.toLowerCase().includes(searchLower) ||
                    request.vendor?.toLowerCase().includes(searchLower) ||
                    region.toLowerCase().includes(searchLower) ||
                    itemNames.toLowerCase().includes(searchLower)
                );
            });
        }

        // Status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(request => request.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== 'All' && dateFilter !== '') {
            const now = new Date();
            filtered = filtered.filter(request => {
                if (!request.createdAt) return false;
                const requestDate = request.createdAt.toDate ? request.createdAt.toDate() : new Date(request.createdAt);

                switch (dateFilter) {
                    case 'Daily':
                        return requestDate.toDateString() === now.toDateString();
                    case 'Weekly':
                        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        return requestDate >= weekAgo;
                    case 'Monthly':
                        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                        return requestDate >= monthAgo;
                    case 'Custom':
                        const start = startDate ? new Date(startDate) : null;
                        const end = endDate ? new Date(endDate) : null;
                        if (start) start.setHours(0, 0, 0, 0);
                        if (end) {
                            end.setHours(23, 59, 59, 999);
                        }

                        if (start && end) return requestDate >= start && requestDate <= end;
                        if (start) return requestDate >= start;
                        if (end) return requestDate <= end;
                        return true;
                    default:
                        return true;
                }
            });
        }

        setFilteredRequests(filtered);
    }, [requests, searchTerm, statusFilter, dateFilter, startDate, endDate, branchesMap]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const paginatedRequests = filteredRequests.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, dateFilter, startDate, endDate]);

    const handleViewDetails = (request: BAMSRequest) => {
        setSelectedRequest(request);
        setEditedRequest({ ...request });
        setIsEditing(false);
        setShowDetailsModal(true);
    };

    // Download Word document for Additional requests
    const handleDownloadWord = async (request: BAMSRequest) => {
        try {
            const branch = request.branchId ? branchesMap[request.branchId] : null;
            const region = branch?.locationName || request.region || 'N/A';

            // Create header with teal background
            const headerParagraph = new Paragraph({
                children: [
                    new TextRun({
                        text: "Approval for Installation of Additional BAMS Equipment – Branch code " + (request.branchCode || '______') + " Branch Name " + (request.branchName || '______') + " Region " + region,
                        bold: true,
                        size: 24,
                        color: "FFFFFF",
                    }),
                ],
                alignment: AlignmentType.LEFT,
                shading: {
                    fill: "20B2AA",
                },
                spacing: { after: 400 },
                indent: { left: 144 }, // 0.25 inch indent
            });

            // Create hardware details heading
            const hardwareDetailsHeading = new Paragraph({
                children: [
                    new TextRun({
                        text: "Hardware details",
                        bold: true,
                        size: 22,
                        underline: {},
                    }),
                ],
                spacing: { before: 200, after: 400 },
            });

            // Create table header row with teal background
            const headerRow = new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Ser", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Code", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Branch Name", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Cluster", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Region", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Vendor", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Additional Equipment", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Quantity", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Rate", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Amount", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                    new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: "Reason / Rationale for Installation", bold: true, color: "FFFFFF" })] })],
                        shading: { fill: "20B2AA" }
                    }),
                ],
            });

            // Create table rows
            const tableRows: TableRow[] = [headerRow];

            // Add data rows
            if (request.items && request.items.length > 0) {
                request.items.forEach((item, index) => {
                    tableRows.push(
                        new TableRow({
                            children: [
                                new TableCell({ children: [new Paragraph(String(index + 1))] }),
                                new TableCell({ children: [new Paragraph(item.itemCode || 'N/A')] }),
                                new TableCell({ children: [new Paragraph(request.branchName || 'N/A')] }),
                                new TableCell({ children: [new Paragraph(branch?.cluster || 'N/A')] }),
                                new TableCell({ children: [new Paragraph(region)] }),
                                new TableCell({ children: [new Paragraph(request.vendor || 'N/A')] }),
                                new TableCell({ children: [new Paragraph(item.itemName || 'N/A')] }),
                                new TableCell({ children: [new Paragraph(String(item.quantity || 0))] }),
                                new TableCell({ children: [new Paragraph(item.rate ? String(item.rate) : 'N/A')] }),
                                new TableCell({ children: [new Paragraph(item.amount ? String(item.amount) : 'N/A')] }),
                                new TableCell({ children: [new Paragraph(item.reason || 'N/A')] }),
                            ],
                        })
                    );
                });
            } else {
                // Add empty rows if no items
                for (let i = 0; i < 4; i++) {
                    tableRows.push(
                        new TableRow({
                            children: [
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                                new TableCell({ children: [new Paragraph("")] }),
                            ],
                        })
                    );
                }
            }

            // Create table
            const table = new Table({
                rows: tableRows,
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1 },
                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                    left: { style: BorderStyle.SINGLE, size: 1 },
                    right: { style: BorderStyle.SINGLE, size: 1 },
                    insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                    insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                },
            });

            // Create document
            const doc = new Document({
                sections: [
                    {
                        properties: {},
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "ADDITIONAL BAMS EQUIPMENT APPROVAL",
                                        bold: true,
                                        size: 24,
                                        color: "FFFFFF",
                                    }),
                                ],
                                alignment: AlignmentType.CENTER,
                                shading: {
                                    fill: "20B2AA",
                                },
                                spacing: { after: 0 },
                            }),
                            headerParagraph,
                            hardwareDetailsHeading,
                            table,
                        ],
                    },
                ],
            });

            // Generate and download
            const blob = await Packer.toBlob(doc);
            const fileName = `BAMS_Additional_Equipment_Approval_${request.requestId || request.id}.docx`;
            saveAs(blob, fileName);

            setToastMessage('Word document downloaded successfully');
            setToastType('success');
            setShowToast(true);
        } catch (error) {
            console.error('Error generating Word document:', error);
            setToastMessage('Failed to generate Word document');
            setToastType('error');
            setShowToast(true);
        }
    };

    const handleEdit = () => {
        if (!canEdit) {
            setToastMessage('You do not have permission to edit hardware requests');
            setToastType('error');
            setShowToast(true);
            return;
        }
        if (selectedRequest) {
            setEditedRequest({ ...selectedRequest });
        }
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (selectedRequest) {
            setEditedRequest({ ...selectedRequest });
        }
    };

    const handleApprove = async () => {
        if (!companyId || !selectedRequest || !uid || isActionLoading) return;

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                status: 'approved',
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request approved successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error approving request:', error);
            setToastMessage('Failed to approve request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleReject = () => {
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleDownloadEmailPDF = async (request: BAMSRequest) => {
        if (!companyId) {
            setToastMessage('Company ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        try {
            // Get vendor information
            let selectedVendor: any = null;
            if (request.vendorId) {
                try {
                    const vendorDoc = await getDoc(doc(db, 'companies', request.vendorId));
                    if (vendorDoc.exists()) {
                        selectedVendor = { id: vendorDoc.id, ...vendorDoc.data() };
                    }
                } catch (error) {
                    console.error('Error fetching vendor:', error);
                }
            }

            // Get branch PSO information
            let branchPsoName: string = '';
            let branchPsoRegion: string = '';

            if (request.branchId && companyId) {
                try {
                    const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', request.branchId));
                    if (branchDoc.exists()) {
                        const branchData = branchDoc.data();
                        const branchPsoId = branchData.psoId;

                        if (branchPsoId && typeof branchPsoId === 'string') {
                            const branchPsoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', branchPsoId));
                            if (branchPsoDoc.exists()) {
                                const branchPsoData = branchPsoDoc.data();
                                branchPsoName = branchPsoData.name || '';

                                // Extract region from designation
                                const designation = branchPsoData.designation || '';
                                if (designation.includes('North')) {
                                    branchPsoRegion = 'North Region';
                                } else if (designation.includes('South')) {
                                    branchPsoRegion = 'South Region';
                                } else if (designation.includes('East')) {
                                    branchPsoRegion = 'East Region';
                                } else if (designation.includes('West')) {
                                    branchPsoRegion = 'West Region';
                                } else if (designation.includes('Central')) {
                                    branchPsoRegion = 'Central Region';
                                } else {
                                    branchPsoRegion = designation || 'Region';
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branch PSO:', error);
                }
            }

            // Generate email HTML
            let emailBody: string;

            if (isInchargeBAMS) {
                // For incharge users, use purchase order format
                const poNumber = generatePurchaseOrderNumber();
                const poDate = new Date();

                // Get incharge info
                const inchargeInfo = {
                    name: psoName || 'Incharge',
                    email: pso?.email || ''
                };

                emailBody = formatBAMSPurchaseOrderEmail(request, selectedVendor, branches, inchargeInfo, poNumber, poDate, inventoryItems, branchPsoName, branchPsoRegion, taxPercentage);
            } else {
                // For regular PSO/Regional, use regular email format
                emailBody = formatBAMSEmailBody(request, selectedVendor, branches);
            }

            // Split HTML into three parts: form fields, items table with totals, and terms/letter content
            const itemsSectionStart = emailBody.indexOf('<div class="items-section">');
            const termsStart = emailBody.indexOf('<div style="margin-top: 40px;">');
            const beforeItems = emailBody.substring(0, itemsSectionStart);
            const itemsAndTotals = emailBody.substring(itemsSectionStart, termsStart);
            const termsAndLetterContent = emailBody.substring(termsStart);

            // Helper function to add page number to PDF
            const addPageNumber = (pdf: jsPDF, totalPages: number, pageHeight: number) => {
                const pageCount = pdf.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    pdf.setPage(i);
                    pdf.setFontSize(10);
                    pdf.setTextColor(0, 0, 0);
                    pdf.setFont('helvetica', 'normal');
                    // Add page number at bottom center
                    const pageText = `Page ${i} of ${totalPages}`;
                    const textWidth = pdf.getTextWidth(pageText);
                    const pageWidth = pdf.internal.pageSize.getWidth();
                    pdf.text(pageText, (pageWidth - textWidth) / 2, pageHeight - 10);
                }
            };

            // Helper function to render HTML section to PDF
            const renderSectionToPDF = async (htmlContent: string, html2canvas: any, pdf: jsPDF, imgWidth: number, pageHeight: number, startNewPage: boolean = false) => {
                const iframe = document.createElement('iframe');
                iframe.style.position = 'absolute';
                iframe.style.left = '-9999px';
                iframe.style.width = '800px';
                iframe.style.border = 'none';
                document.body.appendChild(iframe);

                await new Promise((resolve) => {
                    iframe.onload = resolve;
                    iframe.srcdoc = htmlContent;
                });

                await new Promise(resolve => setTimeout(resolve, 500));

                const iframeBody = iframe.contentDocument?.body || iframe.contentWindow?.document.body;
                if (!iframeBody) {
                    document.body.removeChild(iframe);
                    throw new Error('Could not access iframe content');
                }

                const canvas = await html2canvas(iframeBody, {
                    scale: 3,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    width: 800,
                    windowWidth: 800,
                    allowTaint: false,
                    foreignObjectRendering: true
                });

                document.body.removeChild(iframe);

                const imgData = canvas.toDataURL('image/png', 1.0);
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight;
                let position = 0;

                if (startNewPage) {
                    pdf.addPage();
                }

                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pageHeight;

                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
                    heightLeft -= pageHeight;
                }
            };

            // Try to use html2canvas if available
            try {
                // Dynamic import for html2canvas
                const html2canvasModule = await import('html2canvas');
                const html2canvas = html2canvasModule.default || html2canvasModule;
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgWidth = 210; // A4 width in mm
                const pageHeight = 297; // A4 height in mm

                // Create wrapper HTML for each section with proper styling
                const firstPageHTML = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                            body { font-family: Arial, sans-serif; line-height: 1.8; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
                            * { color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; }
                            .outer-wrapper { padding: 24px 0; }
                            .container { max-width: 640px; margin: 0 auto; background: white; padding: 24px 24px; border-radius: 8px; }
                            .header { text-align: left; margin-bottom: 30px; border-bottom: 2px solid #000000; padding-bottom: 12px; }
                            .header h1 { margin: 0 0 10px 0; font-size: 24px; color: #000000 !important; font-weight: bold; }
                            .form-section { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
                            .form-group { margin-bottom: 15px; }
                            .form-group label { display: block; font-weight: bold; margin-bottom: 5px; font-size: 12px; color: #000000 !important; }
                            .form-group input, .form-group textarea { width: 100%; padding: 8px; border: 1px solid #000000; border-radius: 4px; font-size: 12px; box-sizing: border-box; color: #000000 !important; background-color: #ffffff !important; font-weight: normal; word-wrap: break-word; overflow-wrap: break-word; }
                            .form-group textarea { min-height: 60px; resize: vertical; }
                            .form-group .hint { font-size: 10px; color: #000000 !important; margin-top: 3px; font-style: italic; }
                            .form-group input[readonly], .form-group textarea[readonly] { color: #000000 !important; background-color: #f5f5f5 !important; -webkit-text-fill-color: #000000 !important; }
                        </style>
                    </head>
                    <body>
                        <div class="outer-wrapper">
                            <div class="container">
                                ${beforeItems}
                            </div>
                        </div>
                    </body>
                    </html>
                `;

                const secondPageHTML = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                            body { font-family: Arial, sans-serif; line-height: 1.8; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
                            * { color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; }
                            .outer-wrapper { padding: 24px 0; }
                            .container { max-width: 640px; margin: 0 auto; background: white; padding: 24px 24px; border-radius: 8px; }
                            .items-section { margin-top: 0; }
                            .items-section h3 { margin-bottom: 15px; font-size: 16px; font-weight: bold; color: #000000 !important; }
                            .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; table-layout: auto; }
                            .items-table th { background-color: #f0f0f0; font-weight: bold; padding: 10px 8px; text-align: left; border: 1px solid #000000; font-size: 11px; color: #000000 !important; word-wrap: break-word; }
                            .items-table td { padding: 8px; border: 1px solid #000000; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 120px; }
                            table { color: #000000 !important; width: 100%; }
                            table td, table th { color: #000000 !important; word-wrap: break-word; }
                        </style>
                    </head>
                    <body>
                        <div class="outer-wrapper">
                            <div class="container">
                                ${itemsAndTotals}
                            </div>
                        </div>
                    </body>
                    </html>
                `;

                const thirdPageHTML = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1.0">
                        <style>
                            body { font-family: Arial, sans-serif; line-height: 1.8; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
                            * { color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; }
                            .outer-wrapper { padding: 24px 0; }
                            .container { max-width: 640px; margin: 0 auto; background: white; padding: 24px 24px; border-radius: 8px; }
                            .letter-content { margin-top: 0; line-height: 1.8; }
                            .letter-content p { margin-bottom: 15px; color: #000000 !important; word-wrap: break-word; }
                            ol, li { color: #000000 !important; word-wrap: break-word; }
                        </style>
                    </head>
                    <body>
                        <div class="outer-wrapper">
                            <div class="container">
                                ${termsAndLetterContent}
                            </div>
                        </div>
                    </body>
                    </html>
                `;

                // Render page 1 (form fields)
                await renderSectionToPDF(firstPageHTML, html2canvas, pdf, imgWidth, pageHeight, false);

                // Render page 2 (items table and totals) - starts on new page
                await renderSectionToPDF(secondPageHTML, html2canvas, pdf, imgWidth, pageHeight, true);

                // Render page 3 (terms and letter content) - starts on new page
                await renderSectionToPDF(thirdPageHTML, html2canvas, pdf, imgWidth, pageHeight, true);

                // Add page numbers to all pages
                const totalPages = pdf.getNumberOfPages();
                addPageNumber(pdf, totalPages, pageHeight);

                const fileName = `BAMS_Request_${request.requestId || request.id}_${new Date().toISOString().split('T')[0]}.pdf`;
                pdf.save(fileName);

                setToastMessage('PDF downloaded successfully');
                setToastType('success');
                setShowToast(true);
            } catch (error) {
                console.error('Error generating PDF:', error);
                setToastMessage('Failed to generate PDF. Please try again.');
                setToastType('error');
                setShowToast(true);
            }
        } catch (error) {
            console.error('Error downloading PDF:', error);
            setToastMessage('Failed to download PDF');
            setToastType('error');
            setShowToast(true);
        }
    };

    const handleConfirmReject = async () => {
        if (!companyId || !selectedRequest || !uid || !rejectReason.trim() || isActionLoading) {
            if (!rejectReason.trim()) {
                setToastMessage('Please provide a reason for rejection');
                setToastType('error');
                setShowToast(true);
            }
            return;
        }

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                status: 'rejected',
                rejectReason: rejectReason.trim(),
                rejectedAt: serverTimestamp(),
                rejectedBy: uid,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request rejected successfully');
            setToastType('success');
            setShowToast(true);
            setShowRejectModal(false);
            setRejectReason('');

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error rejecting request:', error);
            setToastMessage('Failed to reject request');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleMarkAsInstalled = async () => {
        if (!companyId || !selectedRequest || !uid) return;

        if (!installationDate) {
            setToastMessage('Please enter installation date');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                psoStatus: 'installed',
                installationDate: installationDate,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Hardware marked as installed successfully');
            setToastType('success');
            setShowToast(true);
            setShowInstallConfirmModal(false);
            setInstallationDate(new Date().toISOString().split('T')[0]);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error marking as installed:', error);
            setToastMessage('Failed to mark as installed');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!companyId || !editedRequest || !selectedRequest || !uid) return;

        if (!editedRequest.branchCode || !editedRequest.branchName || !editedRequest.vendor || !editedRequest.items || editedRequest.items.length === 0) {
            setToastMessage('Please fill in all required fields and add at least one item');
            setToastType('error');
            setShowToast(true);
            return;
        }

        try {
            // Get region from branch's locationName
            const branch = editedRequest.branchId ? branchesMap[editedRequest.branchId] : null;
            const region = branch?.locationName || '';

            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                branchCode: editedRequest.branchCode,
                branchName: editedRequest.branchName,
                branchId: editedRequest.branchId,
                region: region,
                vendor: editedRequest.vendor,
                vendorId: editedRequest.vendorId,
                items: editedRequest.items,
                remarks: editedRequest.remarks,
                requestCategory: editedRequest.requestCategory,
                dateOfRequest: editedRequest.dateOfRequest,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('Request updated successfully');
            setToastType('success');
            setShowToast(true);
            setIsEditing(false);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error updating request:', error);
            setToastMessage('Failed to update request');
            setToastType('error');
            setShowToast(true);
        }
    };

    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const getStatusBadge = (status?: string) => {
        if (!status) return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">N/A</span>;

        const statusLower = status.toLowerCase();
        if (statusLower.includes('pending')) {
            return <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">Pending</span>;
        } else if (statusLower.includes('approved') || statusLower.includes('completed')) {
            return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Approved</span>;
        } else if (statusLower.includes('rejected')) {
            return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Rejected</span>;
        } else if (statusLower.includes('progress')) {
            return <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">In Progress</span>;
        } else if (statusLower.includes('forwarded')) {
            return <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">Forwarded</span>;
        }
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">{status}</span>;
    };

    const getPSOStatusBadge = (psoStatus?: string, requestStatus?: string) => {
        if (requestStatus === 'cancelled') {
            return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Cancelled</span>;
        }
        if (!psoStatus || psoStatus === 'not installed') {
            return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Not Installed</span>;
        } else if (psoStatus === 'installed') {
            return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Installed</span>;
        }
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Not Installed</span>;
    };

    const handleMarkAsNotInstalled = async () => {
        if (!companyId || !selectedRequest || !uid) return;

        setIsActionLoading(true);
        try {
            const { updateDoc, serverTimestamp } = await import('firebase/firestore');
            await updateDoc(doc(db, 'banks', companyId, 'hardware_requests', selectedRequest.id), {
                psoStatus: 'not installed',
                installationDate: null,
                updatedAt: serverTimestamp(),
                updatedBy: uid,
            });

            setToastMessage('PSO status updated to Not Installed');
            setToastType('success');
            setShowToast(true);

            // Refresh requests
            const requestsSnap = await getDocs(
                query(
                    collection(db, 'banks', companyId, 'hardware_requests'),
                    orderBy('createdAt', 'desc')
                )
            );
            const requestsList = requestsSnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter((request: any) => request.requestType === 'BAMS') as BAMSRequest[];
            setRequests(requestsList);

            // Update selected request
            const updatedRequest = requestsList.find(r => r.id === selectedRequest.id);
            if (updatedRequest) {
                setSelectedRequest(updatedRequest);
                setEditedRequest(updatedRequest);
            }
        } catch (error) {
            console.error('Error updating PSO status:', error);
            setToastMessage('Failed to update PSO status');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    // Handle Prepare Email
    const handlePrepareEmail = async () => {
        if (!selectedRequest || !companyId) return;

        try {
            // 1. Fetch Vendor Contact Email
            let vendorEmails: string[] = [];
            let vendorData: any = null;

            if (selectedRequest.vendorId) {
                const vendorDoc = await getDoc(doc(db, 'companies', selectedRequest.vendorId));
                if (vendorDoc.exists()) {
                    vendorData = vendorDoc.data();
                    if (vendorData.contactEmail) vendorEmails.push(vendorData.contactEmail);
                    else if (vendorData.email) vendorEmails.push(vendorData.email);
                }
            }

            // 2. Fetch Branch and PSO Info
            let psoEmail = '';
            let psoName = '';
            let psoRegion = '';
            let regionalHeadEmail = '';
            let branchData: any = null;

            if (selectedRequest.branchId) {
                const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', selectedRequest.branchId));
                if (branchDoc.exists()) {
                    branchData = branchDoc.data();

                    // Fetch PSO email
                    if (branchData.psoId) {
                        const psoDoc = await getDoc(doc(db, 'banks', companyId, 'pos', branchData.psoId));
                        if (psoDoc.exists()) {
                            const psoData = psoDoc.data();
                            if (psoData.email) psoEmail = psoData.email;
                            psoName = psoData.name || 'PSO';

                            // Extract region from designation
                            const designation = psoData.designation || '';
                            if (designation.includes('North')) psoRegion = 'North Region';
                            else if (designation.includes('South')) psoRegion = 'South Region';
                            else if (designation.includes('East')) psoRegion = 'East Region';
                            else if (designation.includes('West')) psoRegion = 'West Region';
                            else if (designation.includes('Central')) psoRegion = 'Central Region';
                            else psoRegion = designation || 'Region';

                            // Fetch Regional Head email
                            if (psoData.regionalHeadId) {
                                try {
                                    const regionalHeadDoc = await getDoc(doc(db, 'banks', companyId, 'pos', psoData.regionalHeadId));
                                    if (regionalHeadDoc.exists()) {
                                        const regionalHeadData = regionalHeadDoc.data();
                                        if (regionalHeadData.email) regionalHeadEmail = regionalHeadData.email;
                                    }
                                } catch (rhErr) {
                                    console.error('Error fetching Regional Head email:', rhErr);
                                }
                            }
                        }
                    }
                }
            }

            // 3. Fetch BAMS Incharges of the bank
            let bamsInchargeEmails: string[] = [];
            try {
                const posSnap = await getDocs(collection(db, 'banks', companyId, 'pos'));
                bamsInchargeEmails = posSnap.docs
                    .map(doc => doc.data())
                    .filter(data => data.email &&
                        data.designation?.toLowerCase().includes('incharge') &&
                        data.designation?.toLowerCase().includes('bams'))
                    .map(data => data.email);
            } catch (err) {
                console.error('Error fetching BAMS Incharges:', err);
            }

            // 4. Fetch Requester Email
            let requesterEmail = '';
            if (selectedRequest.createdBy) {
                try {
                    const creatorDoc = await getDoc(doc(db, 'banks', companyId, 'pos', selectedRequest.createdBy));
                    if (creatorDoc.exists()) {
                        const creatorData = creatorDoc.data();
                        if (creatorData.email) requesterEmail = creatorData.email;
                    }
                } catch (err) {
                    console.error('Error fetching requester email:', err);
                }
            }

            // 5. Add Current User Email
            const currentUserEmail = auth.currentUser?.email || '';

            // 6. Combine all emails and deduplicate
            const allEmails = [
                ...vendorEmails,
                psoEmail,
                regionalHeadEmail,
                currentUserEmail,
                requesterEmail,
                ...bamsInchargeEmails
            ].filter(e => e && e.trim() !== '');

            const uniqueEmails = Array.from(new Set(allEmails));
            setEmailRecipients(uniqueEmails.join(', '));

            // 5. Generate Email Subject
            const branchCode = selectedRequest.branchCode || 'N/A';
            const branchAddress = branchData?.address || selectedRequest.branchName || 'N/A';
            const vendorCompany = vendorData?.companyName || selectedRequest.vendor || 'Vendor';
            const subject = `ABEP 2026 WO: New IP Based BAMS Installation M/S ${vendorCompany}-Location ${branchAddress}`;
            setEmailSubject(subject);

            // 6. Generate Email Body
            const emailBodyHtml = formatBAMSInstallationEmail(
                selectedRequest,
                vendorData,
                branchData,
                { name: psoName, email: psoEmail, region: psoRegion },
                selectedRequest.requestCategory
            );
            setEmailBody(emailBodyHtml);

            // 7. Show Email Preview Modal
            setShowEmailPreview(true);
        } catch (error: any) {
            console.error('Error preparing email:', error);
            setToastMessage(`Failed to prepare email: ${error.message || 'Unknown error'}`);
            setToastType('error');
            setShowToast(true);
        }
    };

    // Handle Send Email
    const handleSendEmail = async () => {
        if (!emailRecipients || !emailSubject || !emailBody) {
            setToastMessage('Email recipients, subject, or body is missing');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSendingEmail(true);
        try {
            await sendEmailsWithErrorHandling({
                to: emailRecipients.split(',').map(e => e.trim()).filter(e => e !== ''),
                subject: emailSubject,
                html: emailBody
            });

            setToastMessage('Email sent successfully');
            setToastType('success');
            setShowToast(true);
            setShowEmailPreview(false);
        } catch (error: any) {
            console.error('Error sending email:', error);
            setToastMessage(`Failed to send email: ${error.message || 'Unknown error'}`);
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSendingEmail(false);
        }
    };


    const exportToExcel = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const dataToExport: any[] = [];

        filteredRequests.forEach((request, index) => {
            // Add Request Header Row
            dataToExport.push({
                'Type': 'REQUEST',
                'Request ID': request.requestId || request.id,
                'Date': request.dateOfRequest || 'N/A',
                'Branch Code': request.branchCode || 'N/A',
                'Branch Name': request.branchName || 'N/A',
                'Vendor': request.vendor || 'N/A',
                'Category': request.requestCategory || 'N/A',
                'Status': request.status || 'N/A',
                'PSO Status': request.psoStatus || 'Not Installed',
                'Remarks': request.remarks || 'N/A',
                'Item Code': '',
                'Item Name': '',
                'Quantity': '',
                'Unit Price': '',
                'Total Price': '',
                'Tax %': '',
                'Tax Amount': '',
                'Price with Tax': ''
            });

            // Add Items
            const items = request.items && Array.isArray(request.items) && request.items.length > 0 ? request.items : null;

            if (items) {
                items.forEach((item: any) => {
                    const quantity = item.quantity || 0;

                    // Look up the rate from inventoryItems using itemId
                    const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                    const rate = inventoryItem?.rates || 0;

                    const unitPrice = rate;
                    const totalPrice = unitPrice * quantity;
                    const taxPercent = taxPercentage || 18;
                    const taxAmount = (totalPrice * taxPercent) / 100;
                    const priceWithTax = totalPrice + taxAmount;

                    dataToExport.push({
                        'Type': 'ITEM',
                        'Request ID': '',
                        'Date': '',
                        'Branch Code': '',
                        'Branch Name': '',
                        'Vendor': '',
                        'Category': '',
                        'Status': '',
                        'PSO Status': '',
                        'Remarks': '',
                        'Item Code': item.itemCode || 'N/A',
                        'Item Name': item.itemName || 'N/A',
                        'Quantity': quantity,
                        'Unit Price': `Rs. ${unitPrice.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Total Price': `Rs. ${totalPrice.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Tax %': `${taxPercent}%`,
                        'Tax Amount': `Rs. ${taxAmount.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        'Price with Tax': `Rs. ${priceWithTax.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    });
                });
            } else {
                dataToExport.push({
                    'Type': 'ITEM',
                    'Request ID': '',
                    'Date': '',
                    'Branch Code': '',
                    'Branch Name': '',
                    'Vendor': '',
                    'Category': '',
                    'Status': '',
                    'PSO Status': '',
                    'Remarks': '',
                    'Item Code': 'N/A',
                    'Item Name': 'N/A',
                    'Quantity': 0,
                    'Unit Price': 'Rs. 0.00',
                    'Total Price': 'Rs. 0.00',
                    'Tax %': '0%',
                    'Tax Amount': 'Rs. 0.00',
                    'Price with Tax': 'Rs. 0.00'
                });
            }

            // Add empty row between requests (except after last one)
            if (index < filteredRequests.length - 1) {
                dataToExport.push({
                    'Type': '',
                    'Request ID': '',
                    'Date': '',
                    'Branch Code': '',
                    'Branch Name': '',
                    'Vendor': '',
                    'Category': '',
                    'Status': '',
                    'PSO Status': '',
                    'Remarks': '',
                    'Item Code': '',
                    'Item Name': '',
                    'Quantity': '',
                    'Unit Price': '',
                    'Total Price': '',
                    'Tax %': '',
                    'Tax Amount': '',
                    'Price with Tax': ''
                });
            }
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'BAMS Requests');

        const fileName = `BAMS_Requests_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const exportToPDF = () => {
        if (filteredRequests.length === 0) {
            setToastMessage('No requests to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text('BAMS HARDWARE REQUESTS', 20, yPosition);
        yPosition += 15;

        const tableData = filteredRequests.map(request => [
            request.requestId || request.id,
            request.dateOfRequest || 'N/A',
            `${request.branchCode}\n${request.branchName}`,
            request.vendor || 'N/A',
            request.items?.map(i => i.itemName).join(', ') || 'N/A',
            request.status || 'N/A',
            request.psoStatus || 'Not Installed',
        ]);

        autoTable(doc, {
            head: [['ID', 'Date', 'Branch', 'Vendor', 'Items', 'Status', 'PSO Status']],
            body: tableData,
            startY: yPosition,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [52, 152, 219], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 249, 250] },
            margin: { left: 15, right: 15 },
        });

        const fileName = `BAMS_Requests_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const columns: Column<BAMSRequest>[] = [
        {
            header: 'Branch/Premises Code',
            accessor: 'branchCode',
            className: 'whitespace-nowrap font-medium text-gray-900 dark:text-gray-100',
        },
        {
            header: 'Branch/Premises Name',
            accessor: 'branchName',
        },
        {
            header: 'Region',
            render: (request: BAMSRequest) => {
                const branch = request.branchId ? branchesMap[request.branchId] : null;
                return branch?.locationName || 'N/A';
            },
        },
        {
            header: 'BAMS Vendor Name',
            accessor: 'vendor',
        },
        {
            header: 'Item Names',
            render: (request: BAMSRequest) => {
                const itemNames = request.items && request.items.length > 0
                    ? request.items.map(item => item.itemName).join(', ')
                    : 'N/A';
                return (
                    <div className="max-w-xs">
                        <p className="truncate" title={itemNames}>
                            {itemNames}
                        </p>
                    </div>
                );
            },
        },
        {
            header: 'Status',
            render: (request: BAMSRequest) => getStatusBadge(request.status),
        },
        {
            header: 'PSO Status',
            render: (request: BAMSRequest) => getPSOStatusBadge(request.psoStatus, request.status),
        },
        {
            header: 'Actions',
            render: (request: BAMSRequest) => (
                <div className="flex justify-end gap-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadEmailPDF(request);
                        }}
                        className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                        title="Download Email as PDF"
                    >
                        <Icon icon={FiFileText} className="h-5 w-5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(request);
                        }}
                        className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-5 w-5" />
                    </button>
                </div>
            ),
            align: 'right',
        },
    ];

    const headerActions = (
        <div className="flex flex-wrap gap-3">
            {canCreateRequests && (
                <Button variant="primary" onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
                    <Icon icon={FiPlus} className="h-4 w-4" />
                    Add Request
                </Button>
            )}
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Search
                </label>
                <TextField
                    type="text"
                    placeholder="Search by ID, branch, requester..."
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    prefix={<Icon icon={FiSearch} className="h-4 w-4" />}
                />
            </div>
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Filter by Status
                </label>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Statuses</option>
                    {statuses.map(status => (
                        <option key={status} value={status} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                            {status}
                        </option>
                    ))}
                </select>
            </div>
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Filter by Date
                </label>
                <select
                    value={dateFilter}
                    onChange={(e) => {
                        setDateFilter(e.target.value);
                        setCurrentPage(1); // Reset page on filter change
                    }}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                    <option value="Daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                    <option value="Weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                    <option value="Monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                    <option value="Custom" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Custom Range</option>
                </select>
            </div>
            {dateFilter === 'Custom' && (
                <>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Start Date
                        </label>
                        <TextField
                            type="date"
                            value={startDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setStartDate(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                            End Date
                        </label>
                        <TextField
                            type="date"
                            value={endDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setEndDate(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </>
            )}
            <div>
                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Tax Percentage (%)
                </label>
                <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={taxPercentage}
                    onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setTaxPercentage(value);
                        localStorage.setItem('bams_tax_percentage', value.toString());
                    }}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                />
            </div>
        </div>
    );

    return (
        <div className="p-6">
            <TableComp
                title="BAMS"
                description="Manage BAMS hardware requests"
                data={paginatedRequests}
                columns={columns}
                keyExtractor={(item) => item.id}
                loading={loading}
                emptyMessage="No BAMS requests found"
                onRowClick={handleViewDetails}
                headerActions={headerActions}
                filters={filters}
                pagination={{
                    currentPage,
                    totalPages,
                    onPageChange: setCurrentPage,
                }}
            />

            {/* Details/Edit Modal */}
            {showDetailsModal && selectedRequest && editedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                        setEditedRequest(null);
                        setIsEditing(false);
                    }}
                    title={isEditing ? `Edit BAMS Request - ${selectedRequest.requestId || selectedRequest.id}` : `BAMS Request Details - ${selectedRequest.requestId || selectedRequest.id}`}
                    size="lg"
                >
                    <div className="p-6">
                        {!isEditing && (
                            <div className="mb-4 flex justify-end gap-2">
                                {selectedRequest.requestCategory === 'Additional' && (
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleDownloadWord(selectedRequest)}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiDownload} className="h-4 w-4" />
                                        Download Word
                                    </Button>
                                )}
                                {isActionLoading && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent"></div>
                                        Processing...
                                    </div>
                                )}
                                {canApproveReject && selectedRequest.status === 'pending' && (
                                    <>
                                        <Button
                                            variant="primary"
                                            onClick={handleApprove}
                                            disabled={isActionLoading}
                                            className="flex items-center gap-2 border-2 border-green-500 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isActionLoading ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                                    Approving...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiCheck} className="h-4 w-4" />
                                                    Approve
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={handleReject}
                                            disabled={isActionLoading}
                                            className="flex items-center gap-2 border-2 border-red-500 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {isActionLoading ? (
                                                <>
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                                    Rejecting...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon icon={FiX} className="h-4 w-4" />
                                                    Reject
                                                </>
                                            )}
                                        </Button>
                                    </>
                                )}
                                {!canApproveReject && canEdit && selectedRequest.status === 'pending' && (
                                    <Button
                                        variant="secondary"
                                        onClick={handleEdit}
                                        disabled={isActionLoading}
                                        className="flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Icon icon={FiEdit2} className="h-4 w-4" />
                                        Edit
                                    </Button>
                                )}
                            </div>
                        )}
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Request ID
                                    </label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {isEditing ? (editedRequest.requestId || 'N/A') : (selectedRequest.requestId || 'N/A')}
                                    </p>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Date of Request {isEditing && <span className="text-red-500">*</span>}
                                    </label>
                                    {isEditing ? (
                                        <TextField
                                            type="date"
                                            value={editedRequest.dateOfRequest || ''}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                setEditedRequest({ ...editedRequest, dateOfRequest: e.target.value })
                                            }
                                            required
                                        />
                                    ) : (
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {selectedRequest.dateOfRequest ? formatDate(new Date(selectedRequest.dateOfRequest)) : 'N/A'}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {isEditing ? (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="relative" ref={branchModalRef}>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch Code <span className="text-red-500">*</span>
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.branchCode || ''}
                                            placeholder="Click to select branch"
                                            readOnly
                                            onClick={() => setShowBranchModal(true)}
                                            required
                                        />
                                        {showBranchModal && (
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                    <TextField
                                                        type="text"
                                                        placeholder="Search branch..."
                                                        value={branchSearch}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBranchSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="max-h-48 overflow-y-auto">
                                                    {filteredBranches.length === 0 ? (
                                                        <div className="p-4 text-center text-sm text-gray-500">No branches found</div>
                                                    ) : (
                                                        filteredBranches.map((branch: any) => (
                                                            <div
                                                                key={branch.id}
                                                                onClick={() => {
                                                                    setEditedRequest({
                                                                        ...editedRequest,
                                                                        branchId: branch.id,
                                                                        branchCode: branch.branchCode || '',
                                                                        branchName: branch.address || branch.name || '',
                                                                        region: branch.locationName || '',
                                                                    });
                                                                    setShowBranchModal(false);
                                                                    setBranchSearch('');
                                                                }}
                                                                className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                            >
                                                                <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch Name
                                        </label>
                                        <TextField
                                            type="text"
                                            value={editedRequest.branchName || ''}
                                            placeholder="Branch Name"
                                            readOnly
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                            {(() => {
                                                const branch = selectedRequest.branchId ? branchesMap[selectedRequest.branchId] : null;
                                                return branch?.locationName || selectedRequest.region || 'N/A';
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isEditing ? (
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Request Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={editedRequest.requestCategory || ''}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                            setEditedRequest({ ...editedRequest, requestCategory: e.target.value as 'Replacement' | 'Additional' | 'New' })
                                        }
                                        required
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                    >
                                        <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Category</option>
                                        <option value="Replacement" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Replacement</option>
                                        <option value="Additional" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Additional</option>
                                        <option value="New" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">New</option>
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Category</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestCategory || 'N/A'}</p>
                                </div>
                            )}

                            {isEditing ? (
                                <div className="relative" ref={vendorModalRef}>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Vendor <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="text"
                                        value={editedRequest.vendor || ''}
                                        placeholder="Click to select vendor"
                                        readOnly
                                        onClick={() => setShowVendorModal(true)}
                                        required
                                    />
                                    {showVendorModal && (
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search vendor..."
                                                    value={vendorSearch}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVendorSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {filteredVendors.length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                                ) : (
                                                    filteredVendors.map(vendor => (
                                                        <div
                                                            key={vendor.id}
                                                            onClick={() => {
                                                                setEditedRequest({
                                                                    ...editedRequest,
                                                                    vendorId: vendor.id,
                                                                    vendor: vendor.companyName || '',
                                                                });
                                                                setShowVendorModal(false);
                                                                setVendorSearch('');
                                                            }}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                            {vendor.companyId && (
                                                                <p className="text-xs text-gray-600 dark:text-gray-400">ID: {vendor.companyId}</p>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Vendor</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.vendor || 'N/A'}</p>
                                </div>
                            )}

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Items {isEditing && <span className="text-red-500">*</span>}
                                </label>
                                {isEditing && (
                                    <Button
                                        variant="secondary"
                                        onClick={handleOpenItemModal}
                                        className="w-full"
                                    >
                                        <Icon icon={FiPlus} className="mr-2 h-4 w-4" />
                                        Add Item
                                    </Button>
                                )}
                                {isEditing && editedRequest.items && editedRequest.items.length > 0 ? (
                                    <div className="mt-3 space-y-2">
                                        {editedRequest.items.map((item, index) => {
                                            // Find item description from inventory items if not available
                                            const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                            const itemDescription = item.itemDescription || inventoryItem?.itemDescription || '';

                                            return (
                                                <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                {item.itemCode} - {item.itemName}
                                                            </p>
                                                            {itemDescription && (
                                                                <p className="mt-1 line-clamp-1 text-xs text-gray-600 dark:text-gray-400">
                                                                    {itemDescription}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="ml-4 flex shrink-0 items-center gap-2">
                                                            <label className="text-xs text-gray-600 dark:text-gray-400">Qty:</label>
                                                            <TextField
                                                                type="number"
                                                                value={item.quantity}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const qty = parseInt(e.target.value) || 1;
                                                                    if (qty >= 1) {
                                                                        const updatedItems = [...(editedRequest.items || [])];
                                                                        updatedItems[index] = {
                                                                            ...item,
                                                                            quantity: qty,
                                                                            amount: item.rate ? item.rate * qty : item.amount
                                                                        };
                                                                        setEditedRequest({ ...editedRequest, items: updatedItems });
                                                                    }
                                                                }}
                                                                min="1"
                                                                className="w-14"
                                                            />
                                                            <button
                                                                onClick={() => {
                                                                    const updatedItems = [...(editedRequest.items || [])];
                                                                    updatedItems.splice(index, 1);
                                                                    setEditedRequest({ ...editedRequest, items: updatedItems });
                                                                }}
                                                                className="rounded-lg p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                                            >
                                                                <Icon icon={FiX} className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {(editedRequest.requestCategory === 'Additional' || editedRequest.requestCategory === 'New' || editedRequest.requestCategory === 'Replacement') && (
                                                        <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-200 pt-3 dark:border-gray-600">
                                                            <div>
                                                                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                    Rate
                                                                </label>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.rate || ''}
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                        const rate = parseFloat(e.target.value) || 0;
                                                                        const amount = rate * (item.quantity || 1);
                                                                        const updatedItems = [...(editedRequest.items || [])];
                                                                        updatedItems[index] = { ...item, rate, amount };
                                                                        setEditedRequest({ ...editedRequest, items: updatedItems });
                                                                    }}
                                                                    min="0"
                                                                    step="0.01"
                                                                    className="w-full text-xs"
                                                                    placeholder="0.00"
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                    Amount
                                                                </label>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.amount || ''}
                                                                    readOnly
                                                                    className="w-full text-xs bg-gray-100 dark:bg-gray-700"
                                                                    placeholder="0.00"
                                                                />
                                                            </div>
                                                            <div className="col-span-3">
                                                                <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                    Reason / Rationale for Installation
                                                                </label>
                                                                <textarea
                                                                    value={item.reason || ''}
                                                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                                                        const updatedItems = [...(editedRequest.items || [])];
                                                                        updatedItems[index] = { ...item, reason: e.target.value };
                                                                        setEditedRequest({ ...editedRequest, items: updatedItems });
                                                                    }}
                                                                    rows={2}
                                                                    className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800"
                                                                    placeholder="Enter reason for installation..."
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : !isEditing && selectedRequest.items && selectedRequest.items.length > 0 ? (
                                    <div className="mt-2 overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 dark:bg-gray-800">
                                                    <th className="border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">S. NO</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Item Name</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Qty</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Unit Price</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Price</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">Tax %</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Tax Amount</th>
                                                    <th className="border border-gray-200 px-3 py-2 text-right text-xs font-semibold text-gray-700 dark:text-gray-300">Price with Tax</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedRequest.items.map((item, index) => {
                                                    // Find item details from inventory items
                                                    const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                                    const itemDescription = item.itemDescription || inventoryItem?.itemDescription || '';
                                                    const unitPriceWithTax = item.rate || inventoryItem?.rates || 0;
                                                    const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;

                                                    // Calculate pricing details
                                                    const quantity = item.quantity || 0;
                                                    const taxRate = itemTaxPercentage / 100;
                                                    const totalPriceWithTax = unitPriceWithTax * quantity;
                                                    const basePrice = totalPriceWithTax / (1 + taxRate);
                                                    const unitBasePrice = quantity > 0 ? basePrice / quantity : 0;
                                                    const tax = totalPriceWithTax - basePrice;

                                                    return (
                                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">{index + 1}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                                <div>
                                                                    <p className="font-medium">{item.itemCode} - {item.itemName}</p>
                                                                    {itemDescription && (
                                                                        <p className="text-xs text-gray-600 dark:text-gray-400">{itemDescription}</p>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">{quantity}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${unitBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${basePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `${itemTaxPercentage}%` : 'N/A'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                                {unitPriceWithTax > 0 ? `Rs. ${totalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {(() => {
                                                    // Calculate grand totals
                                                    let grandTotalBasePrice = 0;
                                                    let grandTotalTax = 0;
                                                    let grandTotalPriceWithTax = 0;

                                                    selectedRequest.items.forEach((item) => {
                                                        const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                                                        const unitPriceWithTax = item.rate || inventoryItem?.rates || 0;
                                                        const itemTaxPercentage = inventoryItem?.taxPercentage ?? taxPercentage;
                                                        const quantity = item.quantity || 0;

                                                        if (unitPriceWithTax > 0) {
                                                            const taxRate = itemTaxPercentage / 100;
                                                            const totalPriceWithTax = unitPriceWithTax * quantity;
                                                            const basePrice = totalPriceWithTax / (1 + taxRate);
                                                            const tax = totalPriceWithTax - basePrice;

                                                            grandTotalBasePrice += basePrice;
                                                            grandTotalTax += tax;
                                                            grandTotalPriceWithTax += totalPriceWithTax;
                                                        }
                                                    });

                                                    return (
                                                        <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
                                                            <td colSpan={3} className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Grand Total:
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                -
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-center text-sm text-gray-900 dark:text-gray-100">
                                                                -
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="border border-gray-200 px-3 py-2 text-right text-sm text-gray-900 dark:text-gray-100">
                                                                Rs. {grandTotalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">No items</p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Remarks
                                </label>
                                {isEditing ? (
                                    <textarea
                                        rows={3}
                                        value={editedRequest.remarks || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            setEditedRequest({ ...editedRequest, remarks: e.target.value })
                                        }
                                        placeholder="Enter any additional remarks..."
                                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {selectedRequest.remarks || 'N/A'}
                                    </p>
                                )}
                            </div>

                            {!isEditing && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                            <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <p className="mt-1">{getPSOStatusBadge(selectedRequest.psoStatus, selectedRequest.status)}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                        </div>
                                        {selectedRequest.psoStatus === 'installed' && selectedRequest.installationDate && (
                                            <div>
                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Installation Date</label>
                                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                    {formatDate(new Date(selectedRequest.installationDate))}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {/* Send Email Button */}
                                    {selectedRequest.status === 'approved' && (
                                        <div className="mt-4">
                                            <Button
                                                variant="primary"
                                                onClick={handlePrepareEmail}
                                                className="flex items-center gap-2"
                                            >
                                                <Icon icon={FiFileText} className="h-4 w-4" />
                                                Send Email
                                            </Button>
                                        </div>
                                    )}
                                    {/* PSO Status buttons - only show to requester */}
                                    {selectedRequest.createdBy === uid && selectedRequest.status !== 'cancelled' && (
                                        <div className="mt-4 space-y-2">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant={selectedRequest.psoStatus === 'installed' ? 'primary' : 'secondary'}
                                                    onClick={() => {
                                                        if (selectedRequest.psoStatus !== 'installed') {
                                                            setShowInstallConfirmModal(true);
                                                        }
                                                    }}
                                                    disabled={isActionLoading || selectedRequest.psoStatus === 'installed'}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Icon icon={FiCheck} className="h-4 w-4" />
                                                    Installed
                                                </Button>
                                                <Button
                                                    variant={selectedRequest.psoStatus === 'not installed' || !selectedRequest.psoStatus ? 'primary' : 'secondary'}
                                                    onClick={handleMarkAsNotInstalled}
                                                    disabled={isActionLoading || selectedRequest.psoStatus === 'not installed' || !selectedRequest.psoStatus}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Icon icon={FiX} className="h-4 w-4" />
                                                    Not Installed
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {selectedRequest.status === 'cancelled' && (
                                        <div className="mt-4">
                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO Status</label>
                                            <p className="mt-2">{getPSOStatusBadge(selectedRequest.psoStatus, selectedRequest.status)}</p>
                                        </div>
                                    )}
                                    {selectedRequest.status === 'rejected' && (selectedRequest.rejectReason || selectedRequest.declineReason) && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Rejection Reason</label>
                                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{selectedRequest.rejectReason || selectedRequest.declineReason}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        {isEditing && (
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        if (!editedRequest.branchCode || !editedRequest.branchName || !editedRequest.vendor || !editedRequest.items || editedRequest.items.length === 0) {
                                            setToastMessage('Please fill in all required fields and add at least one item');
                                            setToastType('error');
                                            setShowToast(true);
                                            return;
                                        }
                                        handleSaveEdit();
                                    }}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Reject Request Modal */}
            {showRejectModal && (
                <Modal
                    isOpen={showRejectModal}
                    onClose={() => {
                        setShowRejectModal(false);
                        setRejectReason('');
                    }}
                    title="Reject Request"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Reason for Rejection <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                rows={4}
                                value={rejectReason}
                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
                                placeholder="Please provide a reason for rejecting this request..."
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            />
                        </div>
                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectReason('');
                                }}
                                disabled={isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirmReject}
                                disabled={isActionLoading}
                                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isActionLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Rejecting...
                                    </>
                                ) : (
                                    'Confirm Reject'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Mark as Installed Confirmation Modal */}
            {showInstallConfirmModal && (
                <Modal
                    isOpen={showInstallConfirmModal}
                    onClose={() => {
                        setShowInstallConfirmModal(false);
                        setInstallationDate(new Date().toISOString().split('T')[0]);
                    }}
                    title="Confirm Installation"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Are you sure you want to mark this hardware request as installed?
                            </p>
                            {selectedRequest && (
                                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">Request ID:</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedRequest.requestId || selectedRequest.id}</p>
                                    <p className="mt-1 text-xs font-semibold text-gray-600 dark:text-gray-400">Branch:</p>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchName || 'N/A'}</p>
                                </div>
                            )}
                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Installation Date <span className="text-red-500">*</span>
                                </label>
                                <TextField
                                    type="date"
                                    value={installationDate}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInstallationDate(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowInstallConfirmModal(false);
                                    setInstallationDate(new Date().toISOString().split('T')[0]);
                                }}
                                disabled={isActionLoading}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                No
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    handleMarkAsInstalled();
                                }}
                                disabled={isActionLoading || !installationDate}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isActionLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                                        Marking...
                                    </>
                                ) : (
                                    'Yes, Mark as Installed'
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Request Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Add New BAMS Hardware Request"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Request ID <span className="text-red-500">*</span>
                                    </label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                        {formData.requestId || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Date of Request <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="date"
                                        value={formData.dateOfRequest}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            setFormData(prev => ({ ...prev, dateOfRequest: e.target.value }))
                                        }
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Request Category <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.requestCategory}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                        setFormData(prev => ({ ...prev, requestCategory: e.target.value as 'Replacement' | 'Additional' | 'New' }))
                                    }
                                    required
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Select Category</option>
                                    <option value="Replacement" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Replacement</option>
                                    <option value="Additional" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Additional</option>
                                    <option value="New" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">New</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative" ref={branchModalRef}>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Branch Code <span className="text-red-500">*</span>
                                    </label>
                                    <TextField
                                        type="text"
                                        value={formData.branchCode}
                                        placeholder="Click to select branch"
                                        readOnly
                                        onClick={() => setShowBranchModal(true)}
                                        required
                                    />
                                    {showBranchModal && (
                                        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                            <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                                <TextField
                                                    type="text"
                                                    placeholder="Search branch..."
                                                    value={branchSearch}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBranchSearch(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                {filteredBranches.length === 0 ? (
                                                    <div className="p-4 text-center text-sm text-gray-500">No branches found</div>
                                                ) : (
                                                    filteredBranches.map((branch: any) => (
                                                        <div
                                                            key={branch.id}
                                                            onClick={() => handleBranchSelection(branch)}
                                                            className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                        >
                                                            <p className="font-medium text-gray-900 dark:text-gray-100">{branch.branchCode}</p>
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">{branch.address || branch.name}</p>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Branch Name
                                    </label>
                                    <TextField
                                        type="text"
                                        value={formData.branchName}
                                        placeholder="Branch Name"
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="relative" ref={vendorModalRef}>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Vendor <span className="text-red-500">*</span>
                                </label>
                                <TextField
                                    type="text"
                                    value={formData.vendor}
                                    placeholder="Click to select vendor"
                                    readOnly
                                    onClick={() => setShowVendorModal(true)}
                                    required
                                />
                                {showVendorModal && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                        <div className="sticky top-0 border-b border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-700">
                                            <TextField
                                                type="text"
                                                placeholder="Search vendor..."
                                                value={vendorSearch}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVendorSearch(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredVendors.length === 0 ? (
                                                <div className="p-4 text-center text-sm text-gray-500">No vendors found</div>
                                            ) : (
                                                filteredVendors.map(vendor => (
                                                    <div
                                                        key={vendor.id}
                                                        onClick={() => handleVendorSelection(vendor)}
                                                        className="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                                                    >
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{vendor.companyName}</p>
                                                        {vendor.companyId && (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">ID: {vendor.companyId}</p>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Items <span className="text-red-500">*</span>
                                </label>
                                <Button
                                    variant="secondary"
                                    onClick={handleOpenItemModal}
                                    className="w-full"
                                >
                                    <Icon icon={FiPlus} className="mr-2 h-4 w-4" />
                                    Add Item
                                </Button>

                                {/* Selected Items List */}
                                {formData.selectedItems.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {formData.selectedItems.map((item, index) => (
                                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            {item.itemCode} - {item.itemName}
                                                        </p>
                                                        {item.itemDescription && (
                                                            <p className="mt-1 line-clamp-1 text-xs text-gray-600 dark:text-gray-400">
                                                                {item.itemDescription}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="ml-4 flex shrink-0 items-center gap-2">
                                                        <label className="text-xs text-gray-600 dark:text-gray-400">Qty:</label>
                                                        <TextField
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                if (qty >= 1) {
                                                                    handleQuantityChange(index, qty);
                                                                }
                                                            }}
                                                            min="1"
                                                            className="w-16"
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveItem(index)}
                                                            className="rounded-lg p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                                        >
                                                            <Icon icon={FiX} className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                {(formData.requestCategory === 'Additional' || formData.requestCategory === 'New' || formData.requestCategory === 'Replacement') && (
                                                    <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-200 pt-3 dark:border-gray-600">
                                                        <div>
                                                            <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                Rate
                                                            </label>
                                                            <TextField
                                                                type="number"
                                                                value={item.rate || ''}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const rate = parseFloat(e.target.value) || 0;
                                                                    const amount = rate * (item.quantity || 1);
                                                                    const updatedItems = [...formData.selectedItems];
                                                                    updatedItems[index] = { ...item, rate, amount };
                                                                    setFormData(prev => ({ ...prev, selectedItems: updatedItems }));
                                                                }}
                                                                min="0"
                                                                step="0.01"
                                                                className="w-full text-xs"
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                Amount
                                                            </label>
                                                            <TextField
                                                                type="number"
                                                                value={item.amount || ''}
                                                                readOnly
                                                                className="w-full text-xs bg-gray-100 dark:bg-gray-700"
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                        <div className="col-span-3">
                                                            <label className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                                                                Reason / Rationale for Installation
                                                            </label>
                                                            <textarea
                                                                value={item.reason || ''}
                                                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                                                    const updatedItems = [...formData.selectedItems];
                                                                    updatedItems[index] = { ...item, reason: e.target.value };
                                                                    setFormData(prev => ({ ...prev, selectedItems: updatedItems }));
                                                                }}
                                                                rows={2}
                                                                className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800"
                                                                placeholder="Enter reason for installation..."
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Remarks
                                </label>
                                <textarea
                                    rows={3}
                                    value={formData.remarks}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                        setFormData(prev => ({ ...prev, remarks: e.target.value }))
                                    }
                                    placeholder="Enter any additional remarks..."
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    if (!formData.branchCode || !formData.branchName || !formData.vendor || formData.selectedItems.length === 0 || !formData.requestCategory) {
                                        setToastMessage('Please fill in all required fields (including Request Category) and add at least one item');
                                        setToastType('error');
                                        setShowToast(true);
                                        return;
                                    }
                                    setShowConfirmModal(true);
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Request'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Add Items Modal */}
            {showItemModal && (
                <Modal
                    isOpen={showItemModal}
                    onClose={() => {
                        setShowItemModal(false);
                        setTempSelectedItems({});
                        setItemSearch('');
                        setItemBrandFilter('All');
                    }}
                    title="Select BAMS Items"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Filter by Brand
                                </label>
                                <select
                                    value={itemBrandFilter}
                                    onChange={(e) => setItemBrandFilter(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                >
                                    <option value="All" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Brands</option>
                                    {uniqueBrands.map((brand) => (
                                        <option key={brand} value={brand} className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
                                            {brand}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Search Items
                                </label>
                                <TextField
                                    type="text"
                                    placeholder="Search item..."
                                    value={itemSearch}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemSearch(e.target.value)}
                                    prefix={<Icon icon={FiSearch} className="h-4 w-4" />}
                                />
                            </div>
                        </div>

                        <div className="mb-4 max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                            {availableItems.length === 0 ? (
                                <div className="p-4 text-center text-sm text-gray-500">No BAMS items available</div>
                            ) : (
                                availableItems.map((item: InventoryItem) => {
                                    const isSelected = !!tempSelectedItems[item.id];
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => handleItemToggle(item)}
                                            className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 p-4 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : ''
                                                }`}
                                        >
                                            <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${isSelected
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                {isSelected && <Icon icon={FiCheck} className="h-3 w-3 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                                    {item.itemCode} - {item.itemName}
                                                </p>
                                                {item.itemDescription && (
                                                    <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                                                        {item.itemDescription}
                                                    </p>
                                                )}
                                                {item.brandName && (
                                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                                        Brand: {item.brandName} {item.makeModel ? `- ${item.makeModel}` : ''}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {Object.keys(tempSelectedItems).length > 0 && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-900/20">
                                <p className="text-xs font-semibold text-blue-900 dark:text-blue-100">
                                    {Object.keys(tempSelectedItems).length} item(s) selected
                                </p>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowItemModal(false);
                                    setTempSelectedItems({});
                                    setItemSearch('');
                                    setItemBrandFilter('All');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmitSelectedItems}
                                disabled={Object.keys(tempSelectedItems).length === 0}
                            >
                                Add Selected Items
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <Modal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    title="Confirm Hardware Request"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4">
                            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Please review the request details before submitting:
                            </h3>

                            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Request ID:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.requestId}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Branch:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.branchCode} - {formData.branchName}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Vendor:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.vendor}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Date of Request:</span>
                                    <p className="text-sm text-gray-900 dark:text-gray-100">{formData.dateOfRequest}</p>
                                </div>
                                <div>
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Items:</span>
                                    <div className="mt-1 space-y-1">
                                        {formData.selectedItems.map((item, index) => (
                                            <div key={index} className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs dark:bg-gray-700/50">
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {item.itemCode} - {item.itemName}
                                                </span>
                                                <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                                    Qty: {item.quantity}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-600">
                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            Total Items: <span className="font-bold">{formData.selectedItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                                        </p>
                                    </div>
                                </div>
                                {formData.remarks && (
                                    <div>
                                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">Remarks:</span>
                                        <p className="text-sm text-gray-900 dark:text-gray-100">{formData.remarks}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    handleSaveRequest();
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Confirm & Submit'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Email Preview Modal */}
            {showEmailPreview && (
                <Modal
                    isOpen={showEmailPreview}
                    onClose={() => setShowEmailPreview(false)}
                    title="Email Preview"
                    size="lg"
                >
                    <div className="p-6">
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    To:
                                </label>
                                <TextField
                                    type="text"
                                    value={emailRecipients}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailRecipients(e.target.value)}
                                    placeholder="Recipient emails"
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Subject:
                                </label>
                                <TextField
                                    type="text"
                                    value={emailSubject}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value)}
                                    placeholder="Email subject"
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Email Body Preview:
                                </label>
                                <div
                                    className="max-h-96 overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                                    dangerouslySetInnerHTML={{ __html: emailBody }}
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEmailPreview(false)}
                                disabled={isSendingEmail}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSendEmail}
                                disabled={isSendingEmail}
                            >
                                {isSendingEmail ? 'Sending...' : 'Send Email'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Toast */}
            {showToast && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
}

